import { useState } from 'react';
import type { Definition, RecipeConstraints } from '../api/missions';
import { ca } from '../i18n/ca';
import { changedParams, currentParams, type RecipeParamValues } from './regenerateParams';

const d = ca.design;
const p = ca.design.recipeParams;

/** What the recipe fixes, each in words with its reason (RecipeView.fixed). */
export interface FixedValue {
  param: string;
  words: string;
  reason: string;
}

/** The options a constraint admits (hidden, not greyed), with the current value kept in the list. */
function admitted(list: string[] | null | undefined, all: string[], current: string): string[] {
  const allowed = list ?? all;
  return allowed.includes(current) ? allowed : [current, ...allowed];
}

/**
 * «Paràmetres del tipus» (release D, D11.14): on a scripted mission the hour
 * (where the type leaves it to the creator), the party and the squad, the ways
 * in and out, the supports, the start, the enemy's strength and the date
 * change only by generating the mission again on its own code and seed, and
 * each control offers only what the type declares. What the type fixes is
 * shown fixed with its reason instead of a control.
 */
export function RecipeParams({
  def,
  constraints,
  fixed = [],
  recipeSupports,
  busy = false,
  onRegenerate,
}: {
  def: Definition;
  constraints?: RecipeConstraints | null;
  fixed?: FixedValue[];
  /** The type's own supports, offered when its constraints name none. */
  recipeSupports?: Record<string, number>;
  busy?: boolean;
  onRegenerate?: (params: Record<string, unknown>) => void;
}) {
  const current = currentParams(def);
  const [draft, setDraft] = useState<RecipeParamValues>(current);
  const set = (patch: Partial<RecipeParamValues>) => setDraft((prev) => ({ ...prev, ...patch }));
  const fixedOf = (param: string) => fixed.find((f) => f.param === param);
  const changes = changedParams(current, draft);
  const pending = Object.keys(changes).length > 0;

  const meansAll = Object.keys(p.meansNames);
  const insertions = admitted(constraints?.insertion, meansAll, draft.insertion);
  const extractions = admitted(constraints?.extraction, meansAll, draft.extraction);
  const starts = admitted(
    (constraints?.startFrom ?? ['land', 'sea']).filter((s) => s !== 'point'),
    ['land', 'sea'],
    draft.startFrom,
  );
  const supportKeys = constraints?.supports ?? Object.keys(recipeSupports ?? def.supports ?? {});
  const range = constraints?.players;
  const outside = range && (draft.players < range.min || draft.players > range.max);

  return (
    <div className="stack" role="group" aria-label={p.title}>
      <h3>{p.title}</h3>
      <p className="muted small">{p.hint}</p>

      {!fixedOf('hour') && (
        <label>
          {p.hour}
          <input
            type="number"
            min={0}
            max={23.5}
            step={0.5}
            value={draft.hour ?? ''}
            onChange={(e) =>
              set({ hour: e.target.value === '' ? undefined : Number(e.target.value) })
            }
          />
        </label>
      )}
      {!fixedOf('date') && (
        <label>
          {p.date}
          <input
            type="date"
            value={draft.date ?? ''}
            onChange={(e) => set({ date: e.target.value || undefined })}
          />
        </label>
      )}

      <div className="two">
        <label>
          {p.players}
          <input
            type="number"
            min={1}
            max={200}
            value={draft.players}
            onChange={(e) => set({ players: Math.round(Number(e.target.value) || 1) })}
          />
        </label>
        <label>
          {p.squadSize}
          <input
            type="number"
            min={1}
            max={32}
            value={draft.squadSize}
            onChange={(e) => set({ squadSize: Math.round(Number(e.target.value) || 1) })}
          />
        </label>
      </div>
      {range && (
        <p className={outside ? 'warn small' : 'muted small'}>
          {outside ? p.playersOutside(range.min, range.max) : p.playersRange(range.min, range.max)}
        </p>
      )}

      <div className="two">
        <label>
          {p.insertion}
          <select value={draft.insertion} onChange={(e) => set({ insertion: e.target.value })}>
            {insertions.map((m) => (
              <option key={m} value={m}>
                {p.meansNames[m] ?? m}
              </option>
            ))}
          </select>
        </label>
        <label>
          {p.extraction}
          <select value={draft.extraction} onChange={(e) => set({ extraction: e.target.value })}>
            {extractions.map((m) => (
              <option key={m} value={m}>
                {p.meansNames[m] ?? m}
              </option>
            ))}
          </select>
        </label>
      </div>
      {constraints?.wayOut && <p className="muted small">{constraints.wayOut.reason}</p>}

      {starts.length > 1 ? (
        <label>
          {p.startFrom}
          <select
            value={draft.startFrom}
            onChange={(e) => set({ startFrom: e.target.value as 'land' | 'sea' })}
          >
            {starts.map((s) => (
              <option key={s} value={s}>
                {p.startNames[s] ?? s}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <dl className="facts">
          <dt>{p.startFrom}</dt>
          <dd>{p.startNames[draft.startFrom] ?? draft.startFrom}</dd>
        </dl>
      )}

      {supportKeys.length > 0 && (
        <fieldset className="bare" aria-label={p.supports}>
          <legend>{p.supports}</legend>
          <div className="two">
            {supportKeys.map((key) => (
              <label key={key}>
                {ca.supportNames[key] ?? key}
                <input
                  type="number"
                  min={0}
                  max={9}
                  value={draft.supports[key] ?? 0}
                  onChange={(e) =>
                    set({
                      supports: {
                        ...draft.supports,
                        [key]: Math.max(0, Math.min(9, Math.round(Number(e.target.value) || 0))),
                      },
                    })
                  }
                />
              </label>
            ))}
          </div>
        </fieldset>
      )}

      <label>
        {p.enemyStrength}
        <select
          value={draft.enemyStrength}
          onChange={(e) => set({ enemyStrength: e.target.value })}
        >
          {Object.entries(d.enemyStrengthNames).map(([v, name]) => (
            <option key={v} value={v}>
              {name}
            </option>
          ))}
        </select>
      </label>

      <div className="row">
        <button
          type="button"
          className="primary"
          disabled={!pending || busy || !onRegenerate}
          onClick={() => onRegenerate?.(changes)}
        >
          {p.regenerate}
        </button>
        <button type="button" disabled={!pending || busy} onClick={() => setDraft(current)}>
          {p.undo}
        </button>
      </div>
    </div>
  );
}
